import { useState, useEffect } from "react"
import {
  X,
  Thermometer,
  Droplets,
  Wind,
  Eye,
  Gauge,
  Sun,
  CloudRain,
  Sunrise,
  Sunset,
  MapPin,
} from "lucide-react"
import { GlassPanel } from "@/components/ui/glass-panel"
import { cn } from "@/lib/utils"
import { useLocation } from "@/components/location-provider"
import { useUnit } from "@/components/unit-provider"

interface QuickLookProps {
  weather: Record<string, unknown> | null
  aqi: Record<string, unknown> | null
}

export function RightInfoPanel({ weather, aqi }: QuickLookProps) {
  const [open, setOpen] = useState(false)
  const { current: location } = useLocation()
  const { display } = useUnit()

  useEffect(() => {
    const saved = localStorage.getItem("right-panel-open")
    if (saved === "true") setOpen(true)
  }, [])

  const toggle = () => {
    setOpen((prev) => {
      localStorage.setItem("right-panel-open", String(!prev))
      return !prev
    })
  }

  if (!weather) return null

  const current = weather.current as Record<string, unknown>
  const aqiData = (aqi as { list?: { main?: { aqi: number }; components?: Record<string, number> }[] })?.list?.[0]

  const aqiLevel = aqiData?.main?.aqi || 1
  const aqiLabels = ["Good", "Fair", "Moderate", "Poor", "Very Poor"]
  const aqiColors = [
    "bg-emerald-500",
    "bg-lime-500",
    "bg-amber-500",
    "bg-orange-500",
    "bg-red-500",
  ]

  return (
    <>
      <button
        onClick={toggle}
        className={cn(
          "fixed right-4 top-20 z-30 flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-300",
          open
            ? "bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20"
            : "bg-background/60 backdrop-blur-xl border border-border/30 hover:border-border/60"
        )}
        title={open ? "Close info panel" : "Open info panel"}
      >
        {open ? <X className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
      </button>

      {open && (
        <aside className="hidden xl:flex w-72 flex-col border-l border-border/30 bg-background/60 backdrop-blur-xl overflow-y-auto animate-scale-in">
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                Quick Look
              </h3>
            </div>

            {/* Location */}
            <GlassPanel intensity="low" className="p-3 flex items-center gap-2.5">
              <MapPin className="h-3.5 w-3.5 text-primary/70" />
              <span className="text-xs font-medium">{location.name}, {location.country}</span>
            </GlassPanel>

            {/* Temperature */}
            <QuickStat
              icon={Thermometer}
              label="Temperature"
              value={display(current.temp as number)}
              sub={`Feels like ${display(current.feels_like as number)}`}
            />

            {/* Wind */}
            <QuickStat
              icon={Wind}
              label="Wind"
              value={`${current.wind_speed} m/s`}
              sub={`${current.wind_deg}° ${getWindDirection(current.wind_deg as number)}`}
            />

            {/* Humidity */}
            <QuickStat
              icon={Droplets}
              label="Humidity"
              value={`${current.humidity}%`}
              sub={`Dew point ${display(current.dew_point as number)}`}
            />

            {/* Visibility */}
            <QuickStat
              icon={Eye}
              label="Visibility"
              value={`${((current.visibility as number) / 1000).toFixed(1)} km`}
            />

            {/* Pressure */}
            <QuickStat
              icon={Gauge}
              label="Pressure"
              value={`${current.pressure} hPa`}
            />

            {/* UV Index */}
            <QuickStat
              icon={Sun}
              label="UV Index"
              value={`${current.uvi}`}
              sub={getUvLabel(current.uvi as number)}
            />

            {/* Sun times */}
            <GlassPanel intensity="low" className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Sunrise className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-xs font-medium">
                  {new Date((current.sunrise as number) * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Sunset className="h-3.5 w-3.5 text-orange-400" />
                <span className="text-xs font-medium">
                  {new Date((current.sunset as number) * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </span>
              </div>
            </GlassPanel>

            {/* AQI */}
            {aqiData && (
              <GlassPanel intensity="low" className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Air Quality</span>
                  <div className="flex items-center gap-1.5">
                    <span className={cn("h-2 w-2 rounded-full", aqiColors[aqiLevel - 1])} />
                    <span className="text-xs font-semibold">{aqiLabels[aqiLevel - 1]}</span>
                  </div>
                </div>
                {aqiData.components && (
                  <div className="space-y-1">
                    {Object.entries(aqiData.components)
                      .slice(0, 5)
                      .map(([key, val]) => (
                        <div key={key} className="flex items-center justify-between text-[10px]">
                          <span className="text-muted-foreground uppercase">{key.replace("_", ".")}</span>
                          <span className="font-mono font-medium">{val as number} μg/m³</span>
                        </div>
                      ))}
                  </div>
                )}
              </GlassPanel>
            )}

            {/* Cloud & precip */}
            <GlassPanel intensity="low" className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CloudRain className="h-3.5 w-3.5 text-blue-400" />
                  <span className="text-xs font-medium">Cloud Cover</span>
                </div>
                <span className="text-xs font-mono font-bold">{String(current.clouds ?? 0)}%</span>
              </div>
              {current.rain ? (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Rain (1h)</span>
                  <span className="text-xs font-mono font-bold text-blue-400">
                    {((current.rain as Record<string, unknown>)["1h"] as number) ?? 0} mm
                  </span>
                </div>
              ) : null}
              {current.snow ? (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Snow (1h)</span>
                  <span className="text-xs font-mono font-bold text-slate-300">
                    {((current.snow as Record<string, unknown>)["1h"] as number) ?? 0} mm
                  </span>
                </div>
              ) : null}
            </GlassPanel>

            {/* Data freshness */}
            <div className="text-[10px] text-muted-foreground/50 text-center pt-2">
              Updated {new Date((current.dt as number) * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </div>
          </div>
        </aside>
      )}
    </>
  )
}

function QuickStat({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  sub?: string
}) {
  return (
    <GlassPanel intensity="low" className="p-3">
      <div className="flex items-center gap-2 text-muted-foreground/70 mb-1">
        <Icon className="h-3 w-3" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-base font-bold font-mono">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </GlassPanel>
  )
}

function getWindDirection(deg: number): string {
  const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
  const i = Math.round(deg / 22.5) % 16
  return dirs[i]
}

function getUvLabel(uvi: number): string {
  if (uvi <= 2) return "Low"
  if (uvi <= 5) return "Moderate"
  if (uvi <= 7) return "High"
  if (uvi <= 10) return "Very High"
  return "Extreme"
}
